/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import { searchTermAssociationFetcher } from '@/data/Content/_SearchTermAssociationFetcher';

import { useLocalization } from '@/data/Localization';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { ProductQueryResponse, ResponseProductType } from '@/data/types/Product';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getProductListQueryParameters } from '@/data/utils/getProductListQueryParameters';
import { expand, shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { error as logError } from '@/data/utils/loggerUtil';
import { mapProductData } from '@/data/utils/mapProductData';
import { querySiteContentResource } from 'integration/generated/query';
import { SiteContentResource } from 'integration/generated/query/SiteContentResource';
import type { CommonSuggestions } from 'integration/generated/query/data-contracts';

import { GetServerSidePropsContext } from 'next';
import { useEffect, useMemo, useState } from 'react';
import useSWR, { preload } from 'swr';
import { useDebounce } from 'use-debounce';

type Props = Parameters<SiteContentResource['findSuggestions']>;
type Query = Omit<Props[1], 'suggestType'> & {
	suggestType?: string[]; // Spec wrongly requires string, when it should be an array of strings, requiring this override
};
type PropsProductSuggestions = Parameters<
	SiteContentResource['findProductSuggestionsBySearchTerm']
>;
type QueryProductSuggestions = Omit<PropsProductSuggestions[2], 'pageNumber'> & {
	pageNumber?: string[]; // Spec wrongly requires string, when it should be an array of strings, requiring this override
};

type SuggestionView = {
	identifier?: string;
	entry: EntryView[];
};
type EntryView = {
	label: string;
	href?: string;
};
const dataMap = (data?: CommonSuggestions): SuggestionView[] =>
	data?.suggestionView
		?.slice(0, 10)
		?.map(({ identifier, entry }) => ({
			identifier,
			entry:
				entry?.map(({ fullPath, term, name, seo }: any) => ({
					label: fullPath || term || name,
					...(seo ? seo : {}),
				})) || [],
		}))
		.filter(({ entry }) => entry.length > 0) || [];

/* const dataMapProducts = (data?: CommonSuggestions): SuggestionView[] =>
	data?.suggestionView
		?.flatMap(({ entry }) => ({
			entry:
				entry?.map(
					({ resourceId, thumbnailRaw, thumbnail, seo, name, partNumber, price }: any) => ({
						resourceId,
						thumbnailRaw,
						thumbnail,
						...(seo ? seo : {}),
						name,
						partNumber,
						price: price ? price : [],
					})
				) || [],
		}))
		.filter(({ entry }) => entry.length > 0) || []; */
const dataMapProducts = (data?: ProductQueryResponse) => {
	const products = (extractContentsArray(data) as ResponseProductType[])?.map(mapProductData) ?? [];
	return (
		products?.map((item) => ({
			thumbnailRaw: item.thumbnail,
			...(item.seo ? item.seo : {}),
			...item,
		})) || []
	);
};

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		query,
		params = {},
	}: {
		storeId: Props[0];
		query: Query;
		params: Props[2];
	}): Promise<ReturnType<SiteContentResource['findSuggestions']> | void> => {
		try {
			const data = await querySiteContentResource(pub).findSuggestions(
				storeId,
				query as Props[1],
				params
			);
			return data;
		} catch (error) {
			logError(context?.req, 'SearchNavigation: fetcher: error: %o', error);
		}
	};

const fetcherProductSuggestionsBySearchTerm =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		searchTerm,
		query,
		params = {},
	}: {
		storeId: PropsProductSuggestions[0];
		searchTerm: PropsProductSuggestions[1];
		query: QueryProductSuggestions;
		params: PropsProductSuggestions[3];
	}): Promise<ReturnType<SiteContentResource['findProductSuggestionsBySearchTerm']> | void> => {
		try {
			const data = await querySiteContentResource(pub).findProductSuggestionsBySearchTerm(
				storeId,
				searchTerm,
				query as PropsProductSuggestions[2],
				params
			);
			return data;
		} catch (error) {
			logError(context?.req, 'SearchNavigation: fetcher: error: %o', error);
		}
	};

const EMPTY_SUGGESTIONS = {};
export const useSearchNavigation = () => {
	const { settings } = useSettings();
	const { user } = useUser();
	const SearchLocalization = useLocalization('Routes').Search;
	const router = useNextRouter();
	const { query } = router;
	const params = useExtraRequestParameters();
	const filteredParams = useMemo(() => getProductListQueryParameters(query), [query]);
	const [searchValue, setSearchValue] = useState<string>(
		() => filteredParams.searchTerm?.toString().trim() ?? ''
	);
	const {
		storeId,
		langId,
		defaultCatalogId: catalogId,
		defaultCurrency: currency,
	} = getClientSideCommon(settings, router);
	const [hasMounted, setHasMounted] = useState(false);
	const [inputChange, onInputChange] = useState('');
	const [debouncedValue] = useDebounce(inputChange, 500);

	useEffect(() => {
		setSearchValue(debouncedValue);
	}, [debouncedValue]);

	useEffect(() => {
		if (hasMounted) return;
		if (storeId) {
			setHasMounted(true);
			preload(
				[
					{
						storeId,
						searchTerm: 'tenis',
						query: {
							langId,
							pageSize: 4,
							...getContractIdParamFromContext(user?.context),
						},
						params: {},
					},
				],
				async ([props]) => fetcherProductSuggestionsBySearchTerm(true)({ ...props, params })
			);
		}
	}, [hasMounted, langId, params, searchValue, storeId, user?.context]);

	const { data = EMPTY_SUGGESTIONS } = useSWR(
		storeId && searchValue !== ''
			? [
					{
						storeId,
						query: {
							langId,
							suggestType: ['Keyword', 'Category', 'Product', 'Brand', 'Seller'],
							term: searchValue,
							...getContractIdParamFromContext(user?.context),
						},
						params: {},
					},
			  ]
			: null,
		async ([props]) => fetcher(true)({ ...props, params }),
		{ use: [laggyMiddleWare], dedupingInterval: 30000, revalidateOnFocus: false }
	);

	/* const { data: dataProductsSuggestions = EMPTY_SUGGESTIONS, isLoading } = useSWR(
		storeId && searchValue !== ''
			? [
					{
						storeId,
						searchTerm: searchValue,
						query: {
							langId,
							pageSize: 4,
							...getContractIdParamFromContext(user?.context),
						},
						params: {},
					},
			  ]
			: null,
		async ([props]) => fetcherProductSuggestionsBySearchTerm(true)({ ...props, params }),
		{ use: [laggyMiddleWare] }
	);*/

	const { data: dataProductsSuggestions = EMPTY_SUGGESTIONS, isLoading } = useSWR(
		storeId && searchValue !== ''
			? [
					shrink({
						...filteredParams,
						storeId,
						catalogId,
						langId,
						currency,
						searchTerm: searchValue,
						limit: 4,
						...getContractIdParamFromContext(user?.context),
					}),
			  ]
			: null,
		async ([props]) => productFetcher(true)(expand(props), params),
		{ use: [] }
	);

	const suggest = useMemo(() => dataMap(data), [data]);
	const suggestProducts = useMemo(
		() => dataMapProducts(dataProductsSuggestions as ProductQueryResponse),
		[dataProductsSuggestions]
	);

	const onSubmit = async ({ label, href }: EntryView) => {
		if (label.trim() === '') {
			return;
		}
		const data = await searchTermAssociationFetcher(true)({ storeId, searchTerm: label, params });
		const urlParts = data?.redirecturl
			?.replace(REGEX.SEARCH_REDIRECT_SUFFIX, EMPTY_STRING)
			.split('?');

		if (urlParts?.at(0)) {
			router.push({ pathname: urlParts[0], query: urlParts[1] }, undefined, { shallow: false });
		} else {
			router.push(
				href
					? { pathname: href }
					: {
							pathname: `/${SearchLocalization.route.t()}`,
							query: { searchTerm: encodeURIComponent(label) },
					  },
				undefined,
				{
					shallow: true,
				}
			);
		}
	};

	/**
	 * @deprecated use onInputChange instead.
	 */
	const onChange = (newValue: string) => setSearchValue(newValue);

	// const onInputChange = debounce((_, value) => setSearchValue(value), TYPE_AHEAD_DELAY);

	useEffect(() => {
		setSearchValue(filteredParams.searchTerm?.toString().trim() ?? '');
	}, [filteredParams.searchTerm]);

	return {
		suggest,
		suggestProducts,
		searchValue,
		onChange,
		onInputChange,
		onSubmit,
		isLoading,
	};
};
